import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { TEMA, useMobil } from '../theme';
import {
  InköpDetalj, Person, Rad, Leverans, InköpStatus, KostnadsTyp, Mottagare, Villkor, Uppgift,
  STATUSNAMN, STATUSORDNING, LEVERANSNAMN, KOSTNADSNAMN, MOTTAGARNAMN, PALLNAMN, VILLKORSBAS, villkorDelar,
  skapaUppgift, ändraUppgift, taBortUppgift, bockaKontroll,
  hämtaEttInköp, ändraInköp, taBortInköp, hämtaInköpsanvändare,
  skapaLeverans, ändraLeverans, taBortLeverans, skapaKostnad, taBortKostnad,
  kr, valutabelopp, datumText, felText, idag as idagText,
} from '../api/inkop';
import Langtext from '../components/Langtext';
import { useAuth } from '../context/AuthContext';
import { useDemo } from '../demo';
import { sida, kort, fält, knapp, knappSpök, länkknapp, etikett, märke } from '../components/inkopStil';

const AKTIVA_STATUS: InköpStatus[] = ['dialog', 'bekraftat', 'pagar', 'inlevererat'];
const tom = (v: unknown) => v === '' || v === null || v === undefined;
const nummer = (v: unknown) => (tom(v) ? null : Number(String(v).replace(',', '.')));
const kortFält: React.CSSProperties = { ...fält, width: 'auto', padding: '4px 8px', fontSize: '13px' };

// ISO-veckan för ett datum, eftersom hämtningar oftast bokas per vecka.
function vecka(iso: string): number {
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
  const torsdag = new Date(d);
  torsdag.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const förstaTorsdag = new Date(torsdag.getFullYear(), 0, 4);
  return 1 + Math.round(((torsdag.getTime() - förstaTorsdag.getTime()) / 86400000 - 3 + ((förstaTorsdag.getDay() + 6) % 7)) / 7);
}

export type RadUtkast = {
  vin: string; argang: string; volym_cl: string; alkoholhalt: string;
  antal: string; pris_valuta: string; listpris: string; artikel_nr: string;
  mottagare: Mottagare; reserverad_for: string; prov: boolean; ordinarie_pris_valuta: string;
};
export const TOM_RAD: RadUtkast = {
  vin: '', argang: '', volym_cl: '75', alkoholhalt: '', antal: '', pris_valuta: '', listpris: '', artikel_nr: '',
  mottagare: 'kvh', reserverad_for: '', prov: false, ordinarie_pris_valuta: '',
};

export function radTillUtkast(r: Rad): RadUtkast {
  const s = (v: unknown) => (v == null ? '' : String(v));
  return {
    vin: r.vin, argang: s(r.argang), volym_cl: s(r.volym_cl), alkoholhalt: s(r.alkoholhalt),
    antal: s(r.antal), pris_valuta: s(r.pris_valuta), listpris: s(r.listpris), artikel_nr: s(r.artikel_nr),
    mottagare: r.mottagare || 'kvh', reserverad_for: s(r.reserverad_for), prov: !!r.prov,
    ordinarie_pris_valuta: s(r.ordinarie_pris_valuta),
  };
}

export function utkastTillData(u: RadUtkast) {
  return {
    vin: u.vin.trim(),
    argang: u.argang.trim() || null,
    volym_cl: nummer(u.volym_cl) ?? 75,
    alkoholhalt: nummer(u.alkoholhalt),
    antal: Math.round(nummer(u.antal) ?? 0),
    pris_valuta: nummer(u.pris_valuta),
    listpris: nummer(u.listpris),
    artikel_nr: u.artikel_nr.trim().toUpperCase() || null,
    mottagare: u.mottagare,
    reserverad_for: u.reserverad_for.trim() || null,
    prov: u.prov,
    ordinarie_pris_valuta: nummer(u.ordinarie_pris_valuta),
  };
}

export function RadFormulär({ utkast, setUtkast, onSpara, onAvbryt, valuta }: {
  utkast: RadUtkast; setUtkast: (u: RadUtkast) => void; onSpara: () => void; onAvbryt: () => void; valuta: string;
}) {
  const f = (namn: Exclude<keyof RadUtkast, 'prov' | 'mottagare'>, text: string, bred = false) => (
    <div style={{ gridColumn: bred ? 'span 2' : undefined }}>
      <label style={etikett} htmlFor={`rad-${namn}`}>{text}</label>
      <input id={`rad-${namn}`} style={fält} value={utkast[namn]} onChange={e => setUtkast({ ...utkast, [namn]: e.target.value })} />
    </div>
  );
  return (
    <form
      onSubmit={e => { e.preventDefault(); onSpara(); }}
      style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 110px), 1fr))', gap: '10px', padding: '12px 0', alignItems: 'end' }}
    >
      {f('vin', 'Vin', true)}
      {f('argang', 'Årgång')}
      {f('antal', 'Flaskor')}
      {f('pris_valuta', `Pris per flaska (${valuta})`)}
      {f('listpris', 'Listpris kr')}
      {f('volym_cl', 'Volym cl')}
      {f('alkoholhalt', 'Alkohol %')}
      {f('artikel_nr', 'Artikelnr')}
      <div>
        <label style={etikett} htmlFor="rad-mottagare">Går till</label>
        <select id="rad-mottagare" style={fält} value={utkast.mottagare}
          onChange={e => setUtkast({ ...utkast, mottagare: e.target.value as Mottagare })}>
          {Object.entries(MOTTAGARNAMN).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
        </select>
      </div>
      {f('reserverad_for', 'Vikt för (kund, Systembolaget)', true)}
      {f('ordinarie_pris_valuta', `Ordinarie pris (${valuta})`)}
      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', paddingBottom: '8px' }}>
        <input type="checkbox" style={{ accentColor: TEMA.accent }} checked={utkast.prov}
          onChange={() => setUtkast({ ...utkast, prov: !utkast.prov })} />
        Prover
      </label>
      <div style={{ display: 'flex', gap: '6px' }}>
        <button type="submit" style={knapp} disabled={!utkast.vin.trim()}>Spara</button>
        <button type="button" style={knappSpök} onClick={onAvbryt}>Avbryt</button>
      </div>
    </form>
  );
}

function LeveransKort({ l, rader, valuta, maxPerPall, ladda, setFel }: {
  l: Leverans; rader: Rad[]; valuta: string; maxPerPall?: number | null; ladda: () => void; setFel: (s: string) => void;
}) {
  const [antal, setAntal] = useState<Record<string, string>>({});
  const [redigerar, setRedigerar] = useState(false);
  const [nyKostnad, setNyKostnad] = useState(false);
  const [kostnad, setKostnad] = useState<{ typ: KostnadsTyp; belopp: string; valuta: string }>({ typ: 'frakt', belopp: '', valuta: 'SEK' });

  useEffect(() => {
    setAntal(Object.fromEntries(rader.map(r => [r.id, String(l.rader.find(x => x.rad_id === r.id)?.antal ?? '')])));
  }, [l, rader]);

  const ändra = async (data: Record<string, unknown>) => {
    try {
      await ändraLeverans(l.id, data);
      ladda();
    } catch (e) {
      setFel(felText(e, 'Kunde inte spara leveransen.'));
    }
  };

  async function läggTillKostnad(e: React.FormEvent) {
    e.preventDefault();
    if (!kostnad.belopp) return;
    try {
      await skapaKostnad(l.id, { typ: kostnad.typ, belopp: nummer(kostnad.belopp), valuta: kostnad.valuta });
      setKostnad({ ...kostnad, belopp: '' });
      setNyKostnad(false);
      ladda();
    } catch (err) {
      setFel(felText(err, 'Kunde inte spara kostnaden.'));
    }
  }

  const flaskor = l.flaskor_totalt && l.flaskor_totalt > l.flaskor ? `ca ${l.flaskor_totalt.toLocaleString('sv-SE')}` : l.flaskor.toLocaleString('sv-SE');
  const LEVSTEG = ['planerad', 'bokad', 'pa_vag', 'inlevererad'] as const;
  const nu = LEVSTEG.indexOf(l.status);
  const nästa = nu < LEVSTEG.length - 1 ? LEVSTEG[nu + 1] : null;
  const NÄSTA_TEXT: Record<string, string> = { bokad: 'Hämtningen är bokad', pa_vag: 'Vinet är hämtat', inlevererad: 'Vinet har kommit in' };
  const kostnadKr = l.kostnader.reduce((sum, k) => sum + (k.belopp_kr || 0), 0);
  const pallText = l.pallar
    ? `${l.pallar} ${l.palltyp ? PALLNAMN[l.palltyp].toLowerCase() + (l.pallar > 1 ? 'ar' : '') : l.pallar > 1 ? 'pallar' : 'pall'}`
    : null;
  const fakta: [string, React.ReactNode][] = [
    ['Hämtas', l.planerat_datum
      ? <>v{vecka(l.planerat_datum)} · {datumText(l.planerat_datum, true)}{l.preliminart && <span style={{ color: TEMA.inkFaint }}> · preliminärt</span>}</>
      : <span style={{ color: TEMA.inkFaint }}>Inget datum</span>],
    ['Pallar', pallText
      ? <span style={{ color: l.over_tak ? '#D9695F' : undefined }}>{pallText}{l.flaskor_per_pall ? ` · ${l.flaskor_per_pall} fl/pall` : ''}</span>
      : <span style={{ color: TEMA.inkFaint }}>–</span>],
    ['Flaskor', `${flaskor} fl`],
    ['Kostnader', kostnadKr ? kr(kostnadKr) : <span style={{ color: TEMA.inkFaint }}>Inga än</span>],
  ];

  return (
    <div style={{ padding: '14px 0', borderTop: `1px solid ${TEMA.border}` }}>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ color: TEMA.ink, fontSize: '14.5px' }}>Leverans {l.nummer}</span>
        <span style={märke(l.status === 'inlevererad' ? 'grön' : l.status === 'planerad' ? 'grå' : 'guld')}>{LEVERANSNAMN[l.status]}</span>
        <button style={{ ...länkknapp, marginLeft: 'auto', fontSize: '12.5px' }} onClick={() => setRedigerar(r => !r)}>{redigerar ? 'Klar' : 'Ändra'}</button>
      </div>

      {/* Planerad → Bokad → På väg → Inlevererad, och knappen till nästa. */}
      <div style={{ display: 'flex', gap: '4px', margin: '12px 0 6px' }}>
        {LEVSTEG.map((st, n) => (
          <div key={st} style={{ flex: 1 }}>
            <div style={{ height: '4px', borderRadius: '2px', background: n < nu ? TEMA.positive : n === nu ? TEMA.accent : TEMA.border }} />
            <div style={{ fontSize: '11px', marginTop: '4px', color: n === nu ? TEMA.ink : TEMA.inkFaint }}>{LEVERANSNAMN[st]}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap', margin: '4px 0 10px' }}>
        {nästa && (
          <button style={{ ...knappSpök, padding: '5px 12px', fontSize: '12.5px', borderColor: TEMA.accent, color: TEMA.accent }}
            onClick={() => ändra({ status: nästa })}>
            {NÄSTA_TEXT[nästa]} →
          </button>
        )}
        {nu > 0 && (
          <button style={{ ...länkknapp, fontSize: '12.5px', color: TEMA.inkFaint }} onClick={() => ändra({ status: LEVSTEG[nu - 1] })}>
            ← Tillbaka till {LEVERANSNAMN[LEVSTEG[nu - 1]].toLowerCase()}
          </button>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 140px), 1fr))', gap: '10px 18px', margin: '6px 0 8px' }}>
        {fakta.map(([t, v]) => (
          <div key={t}>
            <span style={{ ...etikett, marginBottom: '2px' }}>{t}</span>
            <div style={{ fontSize: '13.5px', color: TEMA.ink }}>{v}</div>
          </div>
        ))}
      </div>
      {l.over_tak && maxPerPall && <div style={{ fontSize: '12.5px', color: '#D9695F', marginBottom: '6px' }}>Producenten tar högst {maxPerPall} fl per pall.</div>}
      {l.samfrakt && <div style={{ fontSize: '12.5px', color: TEMA.inkSoft, marginBottom: '6px' }}>Samfrakt: {l.samfrakt}</div>}

      {redigerar && (
        <div style={{ padding: '10px 12px', margin: '0 0 10px', border: `1px solid ${TEMA.border}`, borderRadius: '3px', fontSize: '12.5px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 130px), 1fr))', gap: '10px', alignItems: 'start' }}>
            <div>
              <label style={etikett} htmlFor={`l-status-${l.id}`}>Status</label>
              <select id={`l-status-${l.id}`} style={fält} value={l.status} onChange={e => ändra({ status: e.target.value })}>
                {Object.entries(LEVERANSNAMN).map(([v, t]) => <option key={v} value={v}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={etikett} htmlFor={`l-datum-${l.id}`}>Hämtas</label>
              <input id={`l-datum-${l.id}`} type="date" style={fält} value={l.planerat_datum || ''}
                onChange={e => ändra({ planerat_datum: e.target.value || null })} />
            </div>
            <div>
              <label style={etikett} htmlFor={`l-pallar-${l.id}`}>Pallar</label>
              <input id={`l-pallar-${l.id}`} key={`p-${l.pallar ?? ''}`} style={fält} inputMode="numeric" defaultValue={l.pallar ?? ''}
                onBlur={e => { const v = nummer(e.target.value); if (v !== (l.pallar ?? null)) ändra({ pallar: v == null ? null : Math.round(v) }); }} />
            </div>
            <div>
              <label style={etikett} htmlFor={`l-typ-${l.id}`}>Palltyp</label>
              <select id={`l-typ-${l.id}`} style={fält} value={l.palltyp || ''} onChange={e => ändra({ palltyp: e.target.value || null })}>
                <option value="">Inte angiven</option>
                {Object.entries(PALLNAMN).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
              </select>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '26px' }}>
              <input type="checkbox" style={{ accentColor: TEMA.accent }} checked={l.preliminart} onChange={() => ändra({ preliminart: !l.preliminart })} />
              Preliminärt
            </label>
          </div>
          <label style={{ ...etikett, marginTop: '10px' }} htmlFor={`l-samfrakt-${l.id}`}>Samfrakt, om pallen delas med en annan producent</label>
          <input id={`l-samfrakt-${l.id}`} key={`s-${l.samfrakt ?? ''}`} style={fält} defaultValue={l.samfrakt ?? ''}
            onBlur={e => { const v = e.target.value.trim() || null; if (v !== (l.samfrakt ?? null)) ändra({ samfrakt: v }); }} />

          {rader.length > 0 && (
            <>
              <span style={{ ...etikett, marginTop: '12px' }}>Flaskor i leveransen</span>
              {rader.map(r => (
                <div key={r.id} style={{ display: 'flex', gap: '10px', alignItems: 'center', fontSize: '13px', marginBottom: '4px' }}>
                  <input style={{ ...fält, width: '80px' }} inputMode="numeric" value={antal[r.id] ?? ''} aria-label={`Flaskor av ${r.vin}`}
                    onChange={e => setAntal({ ...antal, [r.id]: e.target.value })} />
                  <span>{r.vin} <span style={{ color: TEMA.inkFaint }}>av {r.antal}</span></span>
                </div>
              ))}
            </>
          )}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '10px' }}>
            {rader.length > 0 && (
              <button style={knapp} onClick={async () => {
                await ändra({ rader: Object.fromEntries(Object.entries(antal).map(([k, v]) => [k, Math.round(Number(v) || 0)])) });
                setRedigerar(false);
              }}>Spara flaskor</button>
            )}
            <button
              style={{ ...länkknapp, color: TEMA.inkFaint, marginLeft: 'auto' }}
              onClick={async () => {
                if (!window.confirm(`Ta bort leverans ${l.nummer} med dess kostnader?`)) return;
                try { await taBortLeverans(l.id); ladda(); } catch (e) { setFel(felText(e, 'Kunde inte ta bort leveransen.')); }
              }}
            >Ta bort leveransen</button>
          </div>
        </div>
      )}

      <div style={{ fontSize: '13px' }}>
        {l.kostnader.map(k => (
          <div key={k.id} style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', padding: '3px 0' }}>
            <span>{KOSTNADSNAMN[k.typ]}{k.anteckning ? <span style={{ color: TEMA.inkFaint }}> · {k.anteckning}</span> : null}</span>
            <span style={{ display: 'flex', gap: '10px' }}>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                {k.valuta === 'SEK' ? kr(k.belopp) : `${valutabelopp(k.belopp, k.valuta)} ≈ ${kr(k.belopp_kr)}`}
              </span>
              <button
                style={{ ...länkknapp, color: TEMA.inkFaint, fontSize: '12px' }}
                onClick={async () => {
                  try { await taBortKostnad(k.id); ladda(); } catch (e) { setFel(felText(e, 'Kunde inte ta bort kostnaden.')); }
                }}
              >Ta bort</button>
            </span>
          </div>
        ))}
        {!nyKostnad && (
          <button style={{ ...länkknapp, fontSize: '12.5px', marginTop: '4px' }} onClick={() => setNyKostnad(true)}>+ Kostnad</button>
        )}
        {nyKostnad && <form style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }} onSubmit={läggTillKostnad}>
          <select style={kortFält} value={kostnad.typ} aria-label="Kostnadstyp"
            onChange={e => setKostnad({ ...kostnad, typ: e.target.value as KostnadsTyp })}>
            {Object.entries(KOSTNADSNAMN).filter(([v]) => v !== 'agent').map(([v, t]) => <option key={v} value={v}>{t}</option>)}
          </select>
          <input style={{ ...kortFält, width: '110px' }} inputMode="decimal" placeholder="Belopp" aria-label="Belopp" value={kostnad.belopp}
            onChange={e => setKostnad({ ...kostnad, belopp: e.target.value })} />
          <select style={kortFält} value={kostnad.valuta} aria-label="Valuta" onChange={e => setKostnad({ ...kostnad, valuta: e.target.value })}>
            <option>SEK</option>
            {valuta !== 'SEK' && <option>{valuta}</option>}
          </select>
          <button type="submit" style={{ ...knappSpök, padding: '4px 10px' }}>Lägg till</button>
          <button type="button" style={{ ...länkknapp, fontSize: '12.5px', color: TEMA.inkFaint }} onClick={() => setNyKostnad(false)}>Avbryt</button>
        </form>}
      </div>
    </div>
  );
}

// Villkor för just det här inköpet, när de skiljer sig från producentens
// (t.ex. kontant i förskott mot rabatt). Tom lista = producentens gäller igen.
function VillkorRedigerare({ inköp, kör }: {
  inköp: InköpDetalj; kör: (f: () => Promise<unknown>, fel: string) => Promise<void>;
}) {
  type Utkast = { procent: string; dagar: string; fran: Villkor['fran'] };
  const [utkast, setUtkast] = useState<Utkast[] | null>(null);
  const börja = () => setUtkast(inköp.villkor.map(v => ({ procent: String(Math.round(v.andel * 1000) / 10), dagar: String(v.dagar), fran: v.fran })));
  const summa = (utkast || []).reduce((t, v) => t + (Number(v.procent.replace(',', '.')) || 0), 0);
  if (!utkast) {
    return (
      <div style={{ fontSize: '12.5px', margin: '0 0 12px', paddingBottom: '10px', borderBottom: `1px solid ${TEMA.border}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginBottom: '4px' }}>
          <span style={{ color: TEMA.inkFaint }}>{inköp.egna_villkor ? 'Villkor för det här inköpet' : 'Producentens villkor'}</span>
          <span style={{ display: 'flex', gap: '12px' }}>
            {inköp.egna_villkor && (
              <button style={{ ...länkknapp, fontSize: '12.5px', color: TEMA.inkFaint }}
                onClick={() => kör(() => ändraInköp(inköp.id, { betalningsvillkor: [] }), 'Kunde inte spara villkoren.')}>
                Använd producentens
              </button>
            )}
            <button style={{ ...länkknapp, fontSize: '12.5px' }} onClick={börja}>Ändra</button>
          </span>
        </div>
        {inköp.villkor.length
          ? villkorDelar(inköp.villkor).map(t => <div key={t} style={{ color: TEMA.ink }}>{t}</div>)
          : <div>Inga villkor angivna</div>}
      </div>
    );
  }
  return (
    <div style={{ fontSize: '13px', marginBottom: '12px' }}>
      {utkast.map((v, n) => (
        <div key={n} style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '6px', flexWrap: 'wrap' }}>
          <input style={{ ...kortFält, width: '64px' }} inputMode="decimal" value={v.procent} aria-label="Andel i procent"
            onChange={e => setUtkast(utkast.map((x, i) => (i === n ? { ...x, procent: e.target.value } : x)))} />
          <span>% efter</span>
          <input style={{ ...kortFält, width: '56px' }} inputMode="numeric" value={v.dagar} aria-label="Dagar"
            onChange={e => setUtkast(utkast.map((x, i) => (i === n ? { ...x, dagar: e.target.value } : x)))} />
          <span>dagar från</span>
          <select style={kortFält} value={v.fran} aria-label="Räknat från"
            onChange={e => setUtkast(utkast.map((x, i) => (i === n ? { ...x, fran: e.target.value as Villkor['fran'] } : x)))}>
            {Object.entries(VILLKORSBAS).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
          </select>
          <button style={{ ...länkknapp, fontSize: '12px', color: TEMA.inkFaint }} aria-label="Ta bort delen"
            onClick={() => setUtkast(utkast.filter((_, i) => i !== n))}>×</button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        <button style={länkknapp} onClick={() => setUtkast([...utkast, { procent: '', dagar: '0', fran: 'bestallning' }])}>+ Del</button>
        <span style={{ color: Math.abs(summa - 100) > 1 ? '#D9695F' : TEMA.inkFaint }}>Summa {Math.round(summa * 10) / 10} %</span>
        <button style={{ ...knapp, padding: '4px 12px' }} disabled={Math.abs(summa - 100) > 1}
          onClick={async () => {
            await kör(() => ändraInköp(inköp.id, {
              betalningsvillkor: utkast.map(v => ({ andel: (Number(v.procent.replace(',', '.')) || 0) / 100, dagar: Math.round(Number(v.dagar) || 0), fran: v.fran })),
            }), 'Kunde inte spara villkoren.');
            setUtkast(null);
          }}>Spara för det här inköpet</button>
        <button style={knappSpök} onClick={() => setUtkast(null)}>Avbryt</button>
      </div>
    </div>
  );
}

// Bockar: papper och annat som måste bli gjort innan vinet kan rulla.
function Bockar({ inköp, personer, kör }: {
  inköp: InköpDetalj; personer: Person[]; kör: (f: () => Promise<unknown>, fel: string) => Promise<void>;
}) {
  const [ny, setNy] = useState<{ text: string; leverans_id: string; ansvarig: string; forfaller: string } | null>(null);
  const namn = (id?: string | null) => personer.find(p => p.id === id)?.namn;
  const leverans = (id?: string | null) => inköp.leveranser.find(l => l.id === id);
  const öppna = inköp.uppgifter.filter(u => !u.klar);
  const klara = inköp.uppgifter.filter(u => u.klar);
  const redan = new Set(inköp.uppgifter.map(u => u.text));
  const tom = { text: '', leverans_id: '', ansvarig: inköp.logistikansvarig || '', forfaller: '' };
  const rad = (u: Uppgift) => (
    <div key={u.id} style={{ display: 'flex', gap: '10px', alignItems: 'center', padding: '8px 0', borderTop: `1px solid ${TEMA.border}`, fontSize: '13px' }}>
      <input type="checkbox" style={{ accentColor: TEMA.accent, margin: 0, flexShrink: 0 }} checked={u.klar} aria-label={u.text}
        onChange={() => kör(() => ändraUppgift(u.id, { klar: !u.klar }), 'Kunde inte spara.')} />
      <span style={{ flex: 1, color: u.klar ? TEMA.inkFaint : TEMA.ink, textDecoration: u.klar ? 'line-through' : undefined }}>
        {u.text}
        <span style={{ color: TEMA.inkFaint, fontSize: '12px' }}>
          {[leverans(u.leverans_id) ? `leverans ${leverans(u.leverans_id)!.nummer}` : null, namn(u.ansvarig),
            u.forfaller && !u.klar ? `senast ${datumText(u.forfaller)}` : null, u.klar ? `klar ${datumText(u.klar_tid?.slice(0, 10))}` : null]
            .filter(Boolean).map(t => ` · ${t}`).join('')}
        </span>
      </span>
      <button style={{ ...länkknapp, fontSize: '12px', color: TEMA.inkFaint }} aria-label="Ta bort"
        onClick={() => { if (window.confirm(`Ta bort "${u.text}"?`)) kör(() => taBortUppgift(u.id), 'Kunde inte ta bort.'); }}>×</button>
    </div>
  );
  return (
    <div>
      {inköp.uppgifter.length === 0 && !ny && (
        <p style={{ fontSize: '13px', margin: '0 0 8px' }}>Inget att bocka av. Lägg till faktura, EAD och annat som måste bli gjort.</p>
      )}
      {öppna.map(rad)}
      {klara.map(rad)}
      {!ny && (
        <div style={{ borderTop: inköp.uppgifter.length ? `1px solid ${TEMA.border}` : undefined, paddingTop: '8px' }}>
          <button style={länkknapp} onClick={() => setNy(tom)}>+ Lägg till</button>
        </div>
      )}
      {ny && (
        <form style={{ marginTop: '10px' }} onSubmit={async e => {
          e.preventDefault();
          await kör(() => skapaUppgift(inköp.id, {
            text: ny.text, leverans_id: ny.leverans_id || null, ansvarig: ny.ansvarig || null, forfaller: ny.forfaller || null,
          }), 'Kunde inte spara.');
          setNy(null);
        }}>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
            {inköp.uppgiftsforslag.filter(t => !redan.has(t)).map(t => (
              <button key={t} type="button" onClick={() => setNy({ ...ny, text: t })}
                style={{ ...knappSpök, padding: '3px 9px', fontSize: '12px', borderColor: ny.text === t ? TEMA.accent : TEMA.border }}>{t}</button>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 140px), 1fr))', gap: '8px', alignItems: 'end' }}>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={etikett} htmlFor="u-text">Vad</label>
              <input id="u-text" style={fält} value={ny.text} required onChange={e => setNy({ ...ny, text: e.target.value })} />
            </div>
            {inköp.leveranser.length > 0 && (
              <div>
                <label style={etikett} htmlFor="u-lev">Leverans</label>
                <select id="u-lev" style={fält} value={ny.leverans_id} onChange={e => setNy({ ...ny, leverans_id: e.target.value })}>
                  <option value="">Hela inköpet</option>
                  {inköp.leveranser.map(l => <option key={l.id} value={l.id}>Leverans {l.nummer}</option>)}
                </select>
              </div>
            )}
            <div>
              <label style={etikett} htmlFor="u-ansvarig">Vem</label>
              <select id="u-ansvarig" style={fält} value={ny.ansvarig} onChange={e => setNy({ ...ny, ansvarig: e.target.value })}>
                <option value="">Ingen</option>
                {personer.map(p => <option key={p.id} value={p.id}>{p.namn}</option>)}
              </select>
            </div>
            <div>
              <label style={etikett} htmlFor="u-datum">Senast</label>
              <input id="u-datum" type="date" style={fält} value={ny.forfaller} onChange={e => setNy({ ...ny, forfaller: e.target.value })} />
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button type="submit" style={knapp}>Spara</button>
              <button type="button" style={knappSpök} onClick={() => setNy(null)}>Avbryt</button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}

// Kursen syns tydligt och kan ändras direkt. Pågående inköp följer dagens kurs
// från Riksbanken (beslut 26 okt); en egen kurs gäller tills den återställs.
function KursRad({ inköp, onSpara }: { inköp: InköpDetalj; onSpara: (kurs: number | null) => Promise<void> }) {
  const [ändrar, setÄndrar] = useState(false);
  const [värde, setVärde] = useState('');
  const info = inköp.kurs_info;
  const v = inköp.valuta;
  if (v === 'SEK' || !info) return null;
  const tal4 = (n?: number | null) => (n == null ? '–' : n.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 4 }));
  const fet = (n?: number | null) => <b style={{ color: TEMA.ink, fontWeight: 500 }}>{tal4(n)} kr</b>;
  const text = info.kalla === 'riksbanken'
    ? <>1 {v} = {fet(info.kurs)} · Riksbanken {datumText(info.datum)} · beloppen i kronor är preliminära</>
    : info.kalla === 'egen'
      ? <>Egen kurs: 1 {v} = {fet(info.kurs)}{info.dagens ? ` · Riksbanken ${tal4(info.dagens.kurs)}` : ''}</>
      : info.kalla === 'inkopet'
        ? <>1 {v} = {fet(info.kurs)} · kursen på inköpet{inköp.kurs_datum ? ` (${datumText(inköp.kurs_datum)})` : ''}</>
        : <span style={{ color: '#D9695F' }}>Kurs saknas, beloppen kan inte räknas om till kronor</span>;
  return (
    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', fontSize: '13px', margin: '0 0 16px', padding: '10px 14px', border: `1px solid ${TEMA.border}`, borderRadius: '3px', background: TEMA.bgPanel }}>
      <span>{text}</span>
      {!ändrar && (
        <span style={{ marginLeft: 'auto', display: 'flex', gap: '12px' }}>
          <button style={länkknapp} onClick={() => { setVärde(info.kurs ? String(info.kurs).replace('.', ',') : ''); setÄndrar(true); }}>
            {info.kalla === 'egen' ? 'Ändra' : 'Egen kurs'}
          </button>
          {info.kalla === 'egen' && <button style={länkknapp} onClick={() => onSpara(null)}>Följ dagens kurs</button>}
        </span>
      )}
      {ändrar && (
        <form style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}
          onSubmit={async e => { e.preventDefault(); const n = nummer(värde); if (n && n > 0) { await onSpara(n); setÄndrar(false); } }}>
          <input autoFocus style={{ ...kortFält, width: '100px' }} inputMode="decimal" value={värde} aria-label={`Kronor per ${v}`} onChange={e => setVärde(e.target.value)} />
          <button type="submit" style={{ ...knapp, padding: '4px 12px' }}>Spara</button>
          <button type="button" style={{ ...knappSpök, padding: '4px 10px' }} onClick={() => setÄndrar(false)}>Avbryt</button>
        </form>
      )}
    </div>
  );
}

// Betalningarna läses från leverantörsfakturorna i Fortnox (beslut 27 okt 2026):
// ingen bockar av betalningar i Vinsales. Det som ännu inte fakturerats visas
// som en uppskattning från villkoren och ersätts av fakturan när den kommer.
function Betalningar({ inköp, kör }: { inköp: InköpDetalj; kör: (f: () => Promise<unknown>, fel: string) => Promise<void> }) {
  const b = inköp.betalning;
  const valuta = inköp.valuta;
  const kostnader = inköp.leveranser.flatMap(l => l.kostnader.map(k => ({ ...k, leverans: l.nummer })));
  const radStil: React.CSSProperties = {
    display: 'grid', gridTemplateColumns: '16px minmax(0, 1fr) auto', gap: '10px', alignItems: 'center',
    padding: '8px 0', borderTop: `1px solid ${TEMA.border}`, fontSize: '13px',
  };
  const belopp = (v: number, val: string, kronor?: number | null, ungefär = false) => (
    <span style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
      <span>{val === 'SEK' ? kr(v) : valutabelopp(v, val, 0)}</span>
      {val !== 'SEK' && kronor != null && <span style={{ display: 'block', fontSize: '11.5px', color: TEMA.inkFaint }}>{ungefär ? '≈ ' : ''}{kr(kronor)}</span>}
    </span>
  );
  const andel = b.vantat_valuta ? Math.min(100, (b.betalt_valuta / b.vantat_valuta) * 100) : 0;

  return (
    <div>
      <VillkorRedigerare inköp={inköp} kör={kör} />

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', fontSize: '13px', marginBottom: '6px' }}>
        <span><span style={{ color: TEMA.ink }}>Betalt {valutabelopp(b.betalt_valuta, valuta, 0)}</span> av {inköp.uppskattat ? 'ca ' : ''}{valutabelopp(b.vantat_valuta, valuta, 0)}</span>
        {b.kvar_kr > 0 && <span style={{ color: TEMA.inkSoft }}>Kvar ≈ {kr(b.kvar_kr)}</span>}
      </div>
      <div style={{ height: '6px', borderRadius: '3px', background: TEMA.border, overflow: 'hidden', marginBottom: '14px' }}>
        <div style={{ width: `${andel}%`, height: '100%', background: TEMA.positive }} />
      </div>

      <span style={etikett}>Fakturor i Fortnox</span>
      {!b.kopplad && (
        <p style={{ fontSize: '12.5px', color: TEMA.inkSoft, margin: '4px 0 12px', lineHeight: 1.5 }}>
          Producenten är inte kopplad till någon leverantör i Fortnox än. Leverantörer med samma namn kopplas
          av sig själva; liknande namn finns under <Link to="/inkop/att-atgarda" style={{ color: TEMA.accent }}>Att åtgärda</Link>.
        </p>
      )}
      {b.kopplad && b.fakturor.length === 0 && (
        <p style={{ fontSize: '12.5px', color: TEMA.inkFaint, margin: '4px 0 12px' }}>Inga fakturor från producenten på det här inköpet än.</p>
      )}
      {b.fakturor.map(f => (
        <div key={f.lopnummer} style={radStil}>
          <span style={{ color: f.betald ? TEMA.positive : f.forfallen ? '#D9695F' : TEMA.accent, fontSize: '12px', textAlign: 'center' }}>{f.betald ? '✓' : '●'}</span>
          <span style={{ minWidth: 0 }}>
            <span style={{ color: f.betald ? TEMA.inkSoft : TEMA.ink }}>Faktura {f.fakturanummer || f.lopnummer}</span>
            <span style={{ display: 'block', fontSize: '12px', color: f.forfallen ? '#D9695F' : TEMA.inkFaint }}>
              {f.betald
                ? `Betald${f.betald_datum ? ` ${datumText(f.betald_datum)}` : ''}`
                : `${f.forfallen ? 'Förföll' : 'Förfaller'} ${datumText(f.forfallodag)}${f.saldo !== f.total ? ` · ${valutabelopp(f.saldo, f.valuta, 0)} kvar` : ''}`}
              {` · nr ${f.lopnummer} i Fortnox`}
            </span>
          </span>
          {belopp(f.total, f.valuta, f.total_kr)}
        </div>
      ))}

      {b.prognos.length > 0 && (
        <div style={{ marginTop: '16px' }}>
          <span style={etikett}>Inte fakturerat än, uppskattat från villkoren</span>
          {b.prognos.map((p, n) => {
            const förfallen = p.forfallodag < idagText();
            return (
              <div key={n} style={{ ...radStil, color: TEMA.inkSoft }}>
                <span style={{ color: TEMA.inkFaint, fontSize: '12px', textAlign: 'center' }}>○</span>
                <span style={{ minWidth: 0 }}>
                  <span>{p.beskrivning}{p.delvis ? ' (resten)' : ''}</span>
                  <span style={{ display: 'block', fontSize: '12px', color: förfallen ? '#D9695F' : TEMA.inkFaint }}>
                    {förfallen ? 'Skulle ha betalats' : 'Väntas'} {p.preliminar ? 'ca ' : ''}{datumText(p.forfallodag)}
                    {förfallen ? ' · ingen faktura i Fortnox' : ''}
                  </span>
                </span>
                {belopp(p.belopp_valuta, valuta, p.belopp_kr, true)}
              </div>
            );
          })}
        </div>
      )}
      <p style={{ fontSize: '12px', color: TEMA.inkFaint, margin: '12px 0 0', lineHeight: 1.5 }}>
        Fakturorna läses från Fortnox varje halvtimme och räknas som betalda när saldot är 0. Bokföringen är sanningen; kronorna här är en indikation.
      </p>

      {kostnader.length > 0 && (
        <div style={{ marginTop: '18px' }}>
          <span style={etikett}>Frakt och andra kostnader (till kalkylen)</span>
          {kostnader.map(k => (
            <div key={k.id} style={{ ...radStil, padding: '7px 0' }}>
              <span style={{ color: TEMA.inkFaint, fontSize: '12px', textAlign: 'center' }}>·</span>
              <span>{KOSTNADSNAMN[k.typ]}<span style={{ color: TEMA.inkFaint, fontSize: '12px' }}> · leverans {k.leverans}</span></span>
              <span style={{ whiteSpace: 'nowrap' }}>{k.valuta === 'SEK' ? kr(k.belopp) : `${valutabelopp(k.belopp, k.valuta, 0)} ≈ ${kr(k.belopp_kr)}`}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Var inköpet står. Varje steg säger vad det betyder. Under det steg man är på
// står det som ska vara klart, och knappen för att gå vidare tänds när allt är
// bockat (beslut 27 okt 2026). Admin kan flytta ändå; det loggas.
function Steg({ inköp, kör, admin }: {
  inköp: InköpDetalj; kör: (f: () => Promise<unknown>, fel: string) => Promise<void>; admin: boolean;
}) {
  const ärMobil = useMobil();
  const demo = useDemo();
  const nu = STATUSORDNING.indexOf(inköp.status as InköpStatus);
  const nästa = nu >= 0 && nu < STATUSORDNING.length - 1 ? STATUSORDNING[nu + 1] : null;
  const föregående = nu > 0 ? STATUSORDNING[nu - 1] : null;
  const kontroller = inköp.kontroller[inköp.status] || [];
  const kvar = kontroller.filter(k => !k.klar);
  const flytta = (till: InköpStatus, tvinga = false) =>
    kör(() => ändraInköp(inköp.id, { status: till, ...(tvinga ? { tvinga: true } : {}) }), 'Kunde inte flytta inköpet.');

  if (inköp.status === 'avbrutet') {
    return (
      <div style={{ border: '1px solid #D9695F', borderRadius: '3px', padding: '12px 14px', margin: '14px 0', fontSize: '13px', display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
        <span style={{ color: '#D9695F' }}>Inköpet är avbrutet.</span>
        <button style={länkknapp} onClick={() => flytta('dialog')}>Återuppta i dialog</button>
      </div>
    );
  }
  return (
    <div style={{ margin: '14px 0 6px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: ärMobil ? '1fr 1fr' : `repeat(${STATUSORDNING.length}, minmax(0, 1fr))`, gap: '6px' }}>
        {STATUSORDNING.map((s, n) => {
          const klar = n < nu;
          const aktiv = n === nu;
          return (
            <div key={s} style={{
              padding: '9px 10px', borderRadius: '3px',
              border: `1px solid ${aktiv ? TEMA.accent : TEMA.border}`,
              background: aktiv ? 'rgba(224,168,62,0.10)' : 'transparent',
            }}>
              <div style={{ fontSize: '13px', color: aktiv ? TEMA.ink : klar ? TEMA.inkSoft : TEMA.inkFaint, display: 'flex', gap: '6px', alignItems: 'baseline' }}>
                <span style={{ color: klar ? TEMA.positive : aktiv ? TEMA.accent : TEMA.inkFaint, fontSize: '12px' }}>{klar ? '✓' : `${n + 1}`}</span>
                {STATUSNAMN[s]}
              </div>
              {(aktiv || !ärMobil) && (
                <div style={{ fontSize: '11.5px', color: TEMA.inkFaint, marginTop: '3px', lineHeight: 1.35 }}>{inköp.stegbeskrivning[s]}</div>
              )}
            </div>
          );
        })}
      </div>

      {nästa && (
        <div style={{ border: `1px solid ${TEMA.border}`, borderTop: 'none', borderRadius: '0 0 3px 3px', padding: '12px 14px', marginTop: '-1px' }}>
          <span style={etikett}>Klart innan {STATUSNAMN[nästa].toLowerCase()}</span>
          {kontroller.map(k => (
            <label key={k.nyckel} style={{ display: 'flex', gap: '9px', alignItems: 'baseline', fontSize: '13.5px', padding: '4px 0', cursor: k.automatisk ? 'default' : 'pointer' }}>
              <input type="checkbox" style={{ accentColor: TEMA.accent }} checked={k.klar} disabled={k.automatisk}
                onChange={() => kör(() => bockaKontroll(inköp.id, k.nyckel, !k.klar), 'Kunde inte spara.')} />
              <span style={{ color: k.klar ? TEMA.inkSoft : TEMA.ink }}>
                {k.text}
                {k.automatisk && <span style={{ color: TEMA.inkFaint, fontSize: '12px' }}> · syns i inköpet</span>}
                {/* Vinerna och priserna räknas i Prissättning, tidigt i beställningen (beslut 27 okt 2026). */}
                {!demo && (k.nyckel === 'viner' || k.nyckel === 'pris') && (
                  <Link to={`/inkop/prissattning?producent=${inköp.producent_id}`} onClick={e => e.stopPropagation()}
                    style={{ color: TEMA.accent, textDecoration: 'none', fontSize: '12.5px', marginLeft: '8px' }}>
                    {k.nyckel === 'viner' ? `${inköp.rader.filter(r => !r.prov).length} viner i Prissättning →` : k.klar ? 'Se priserna →' : 'Räkna och lås i Prissättning →'}
                  </Link>
                )}
              </span>
            </label>
          ))}
          <div style={{ display: 'flex', gap: '14px', alignItems: 'center', flexWrap: 'wrap', marginTop: '10px' }}>
            <button style={{ ...knapp, opacity: kvar.length ? 0.45 : 1, cursor: kvar.length ? 'not-allowed' : 'pointer' }}
              disabled={kvar.length > 0} onClick={() => flytta(nästa)}>
              Flytta till {STATUSNAMN[nästa]} →
            </button>
            {kvar.length > 0 && <span style={{ fontSize: '12px', color: TEMA.inkFaint }}>{kvar.length} kvar att bocka av</span>}
            <span style={{ marginLeft: 'auto', display: 'flex', gap: '14px' }}>
              {admin && kvar.length > 0 && (
                <button style={{ ...länkknapp, fontSize: '12.5px', color: TEMA.inkFaint }}
                  onClick={() => { if (window.confirm(`Flytta till ${STATUSNAMN[nästa]} fast ${kvar.length} saker inte är klara? Det loggas.`)) flytta(nästa, true); }}>
                  Flytta ändå
                </button>
              )}
              {föregående && (
                <button style={{ ...länkknapp, fontSize: '12.5px', color: TEMA.inkFaint }} onClick={() => flytta(föregående)}>
                  ← Tillbaka till {STATUSNAMN[föregående].toLowerCase()}
                </button>
              )}
              <button style={{ ...länkknapp, fontSize: '12.5px', color: TEMA.inkFaint }}
                onClick={() => { if (window.confirm('Avbryta inköpet?')) flytta('avbrutet'); }}>Avbryt inköpet</button>
            </span>
          </div>
        </div>
      )}
      {!nästa && föregående && (
        <div style={{ fontSize: '12.5px', marginTop: '8px' }}>
          <button style={{ ...länkknapp, fontSize: '12.5px', color: TEMA.inkFaint }} onClick={() => flytta(föregående)}>
            Öppna igen ({STATUSNAMN[föregående].toLowerCase()})
          </button>
        </div>
      )}
    </div>
  );
}

// En del av inköpet som är hopfälld tills man vill se detaljerna. Raden
// visar det viktigaste även när den är stängd.
function Vik({ titel, sammanfattning, antal, varning, öppen: start = false, åtgärd, children }: {
  titel: string; sammanfattning?: React.ReactNode; antal?: number; varning?: boolean; öppen?: boolean; åtgärd?: React.ReactNode; children: React.ReactNode;
}) {
  const [öppen, setÖppen] = useState(start);
  return (
    <div style={{ borderTop: `1px solid ${TEMA.border}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 0' }}>
        <button onClick={() => setÖppen(ö => !ö)} aria-expanded={öppen} style={{
          flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap',
          background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', fontFamily: "'Inter', sans-serif",
          outline: 'none',
        }}>
          <span style={{ color: TEMA.inkFaint, width: '10px', fontSize: '11px' }}>{öppen ? '▾' : '▸'}</span>
          <span style={{ fontFamily: "'Fraunces', serif", fontSize: '16px', color: TEMA.ink }}>{titel}</span>
          {/* Antal kvar som en piller, som under Att åtgärda. */}
          {!!antal && <span style={{ ...märke('guld'), alignSelf: 'center' }}>{antal}</span>}
          {sammanfattning && <span style={{ fontSize: '12.5px', color: varning ? '#D9695F' : TEMA.inkSoft }}>{sammanfattning}</span>}
        </button>
        {öppen && åtgärd}
      </div>
      {öppen && <div style={{ padding: '0 0 16px 22px' }}>{children}</div>}
    </div>
  );
}

type Inställning = Record<string, string>;

// Ett inköp visas på producentkortet (beslut 27 okt 2026): en producent har
// ett kort, och inköpet är en del av det. Detaljerna ligger i vikar som är
// stängda tills man vill se dem.
export function InköpPanel({ id, onÄndrat, onBorttaget }: { id: string; onÄndrat?: () => void; onBorttaget?: () => void }) {
  const { profil } = useAuth();
  // Kalkylen är hemlig i demon: LS, frakt till kund och övrigt visas inte.
  const demo = useDemo();
  const [i, setI] = useState<InköpDetalj | null>(null);
  const [fel, setFel] = useState<string | null>(null);
  const [personer, setPersoner] = useState<Person[]>([]);
  const [inst, setInst] = useState<Inställning | null>(null);

  const ladda = useCallback(() => {
    hämtaEttInköp(id)
      .then(r => { setI(r.data); setFel(null); })
      .catch(e => setFel(felText(e, 'Kunde inte hämta inköpet.')));
  }, [id]);

  useEffect(() => {
    ladda();
    hämtaInköpsanvändare().then(r => setPersoner(r.data)).catch(() => {});
  }, [ladda]);

  const kör = async (åtgärd: () => Promise<unknown>, felmeddelande: string) => {
    try {
      await åtgärd();
      ladda();
      onÄndrat?.();
    } catch (e) {
      setFel(felText(e, felmeddelande));
    }
  };

  if (fel && !i) return <p style={{ color: '#D9695F' }}>{fel}</p>;
  if (!i) return <p style={{ fontSize: '13px' }}>Laddar inköpet...</p>;

  const inköp = i;
  const valuta = inköp.valuta;
  const namnPå = (pid?: string | null) => personer.find(x => x.id === pid)?.namn || '–';

  function öppnaInställningar() {
    const s = (v: unknown) => (v == null ? '' : String(v));
    setInst({
      rubrik: s(inköp.rubrik), valuta: inköp.valuta,
      agent_procent: s(Math.round((inköp.agent_andel || 0) * 1000) / 10),
      ls: s(inköp.ls), frakt_till_kund: s(inköp.frakt_till_kund), ovrigt: s(inköp.ovrigt),
      bestalld: s(inköp.bestalld), producentansvarig: s(inköp.producentansvarig),
      logistikansvarig: s(inköp.logistikansvarig), anteckning: s(inköp.anteckning),
      senast_i_sverige: s(inköp.senast_i_sverige), kran_vin_datum: s(inköp.kran_vin_datum),
      uppskattat_belopp: s(inköp.uppskattat_belopp), uppskattade_flaskor: s(inköp.uppskattade_flaskor),
    });
  }

  async function sparaInställningar() {
    if (!inst) return;
    await kör(() => ändraInköp(id, {
      rubrik: inst.rubrik, valuta: inst.valuta,
      agent_andel: (nummer(inst.agent_procent) || 0) / 100,
      ls: nummer(inst.ls), frakt_till_kund: nummer(inst.frakt_till_kund), ovrigt: nummer(inst.ovrigt),
      bestalld: inst.bestalld || null, producentansvarig: inst.producentansvarig || null,
      logistikansvarig: inst.logistikansvarig || null, anteckning: inst.anteckning,
      senast_i_sverige: inst.senast_i_sverige || null, kran_vin_datum: inst.kran_vin_datum || null,
      uppskattat_belopp: nummer(inst.uppskattat_belopp),
      uppskattade_flaskor: nummer(inst.uppskattade_flaskor) == null ? null : Math.round(nummer(inst.uppskattade_flaskor)!),
    }), 'Kunde inte spara.');
    setInst(null);
  }

  async function taBort() {
    if (!window.confirm('Ta bort inköpet med rader och leveranser? Det går inte att ångra. (Fakturorna i Fortnox rörs inte.)')) return;
    try {
      await taBortInköp(id);
      onBorttaget?.();
    } catch (e) {
      setFel(felText(e, 'Kunde inte ta bort inköpet.'));
    }
  }

  const idag = new Date().toISOString().slice(0, 10);
  const instFält = (namn: string, text: string, platshållare?: string) => (
    <div>
      <label style={etikett} htmlFor={`inst-${namn}`}>{text}</label>
      <input id={`inst-${namn}`} style={fält} inputMode={namn === 'rubrik' ? undefined : 'decimal'} placeholder={platshållare}
        value={inst?.[namn] ?? ''} onChange={e => inst && setInst({ ...inst, [namn]: e.target.value })} />
    </div>
  );

  // Vikarna har ingen text när de är stängda, bara en varning när något är sent.
  const bet = inköp.betalning;
  const förfallen = bet.forfallet || bet.prognos.some(p => inköp.status !== 'dialog' && p.forfallodag < idag);
  const öppnaBockar = inköp.uppgifter.filter(u => !u.klar).length;
  const titelText = AKTIVA_STATUS.includes(inköp.status) ? 'Pågående inköp' : inköp.status === 'avbrutet' ? 'Avbrutet inköp' : 'Avslutat inköp';
  const underText = [inköp.rubrik, inköp.bestalld ? `beställt ${datumText(inköp.bestalld)}` : `startat ${datumText(inköp.skapad)}`].filter(Boolean).join(' · ');

  return (
    <div style={kort}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: '19px', color: TEMA.ink, margin: 0, fontWeight: 400 }}>{titelText}</h2>
          <div style={{ fontSize: '13px', color: TEMA.inkSoft, marginTop: '3px' }}>
            <span>
              {underText}{' · '}{inköp.uppskattat ? 'ca ' : ''}{inköp.flaskor ? `${inköp.flaskor.toLocaleString('sv-SE')} fl · ` : ''}
              {inköp.uppskattat ? 'ca ' : ''}{valutabelopp(inköp.belopp_valuta, valuta, 0)}
              {valuta !== 'SEK' && inköp.belopp_kr ? ` · ≈ ${kr(inköp.belopp_kr)}` : ''}
            </span>
          </div>
          {(inköp.senast_i_sverige || inköp.kran_vin_datum) && (
            <div style={{ fontSize: '13px', marginTop: '4px' }}>
              {inköp.senast_i_sverige && <span style={{ color: TEMA.ink }}>Måste vara i Sverige {datumText(inköp.senast_i_sverige)}</span>}
              {inköp.senast_i_sverige && inköp.kran_vin_datum && ' · '}
              {inköp.kran_vin_datum && <span>KRAN.vin släpper {datumText(inköp.kran_vin_datum)}</span>}
            </div>
          )}
        </div>
        <button style={{ ...knappSpök, padding: '5px 12px', fontSize: '12.5px' }} onClick={inst ? () => setInst(null) : öppnaInställningar}>
          {inst ? 'Stäng' : 'Inställningar'}
        </button>
      </div>

      <Steg inköp={inköp} kör={kör} admin={profil?.roll === 'admin'} />

      {fel && <p style={{ color: '#D9695F', fontSize: '13px' }}>{fel}</p>}
      {inköp.varning && (
        <p style={{ border: '1px solid #D9695F', color: '#D9695F', borderRadius: '3px', padding: '8px 12px', fontSize: '13px', margin: '0 0 16px' }}>
          {inköp.varning.replace(/\d{4}-\d{2}-\d{2}/g, d => datumText(d, true))}
        </p>
      )}

      {inst && (
        <div style={{ border: `1px solid ${TEMA.border}`, borderRadius: '3px', padding: '14px', margin: '0 0 16px' }}>
          <p style={{ fontSize: '12.5px', margin: '0 0 12px', color: TEMA.inkFaint }}>
            {valuta} · producentansvarig {namnPå(inköp.producentansvarig)} · logistik och ekonomi {namnPå(inköp.logistikansvarig)}
            {inköp.importerat && <> · importerat från {inköp.import_kalla}</>}
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 170px), 1fr))', gap: '14px', marginBottom: '14px' }}>
            {instFält('rubrik', 'Rubrik')}
            <div>
              <label style={etikett} htmlFor="inst-valuta">Valuta</label>
              <select id="inst-valuta" style={fält} value={inst.valuta} onChange={e => setInst({ ...inst, valuta: e.target.value })}>
                <option>EUR</option><option>USD</option><option>SEK</option><option>GBP</option>
              </select>
            </div>
            {instFält('agent_procent', 'Agent, %')}
            {!demo && <>
              {instFält('ls', 'LS kr/fl', `${inköp.standard.ls} (standard)`)}
              {instFält('frakt_till_kund', 'Frakt till kund kr/fl', `${inköp.standard.frakt_till_kund} (standard)`)}
              {instFält('ovrigt', 'Övrigt kr/fl', `${inköp.standard.ovrigt} (standard)`)}
            </>}
            <div>
              <label style={etikett} htmlFor="inst-bestalld">Beställt</label>
              <input id="inst-bestalld" type="date" style={fält} value={inst.bestalld} onChange={e => setInst({ ...inst, bestalld: e.target.value })} />
            </div>
            <div>
              <label style={etikett} htmlFor="inst-senast">Måste vara i Sverige senast</label>
              <input id="inst-senast" type="date" style={fält} value={inst.senast_i_sverige} onChange={e => setInst({ ...inst, senast_i_sverige: e.target.value })} />
            </div>
            <div>
              <label style={etikett} htmlFor="inst-kranvin">Release på KRAN.vin</label>
              <input id="inst-kranvin" type="date" style={fält} value={inst.kran_vin_datum} onChange={e => setInst({ ...inst, kran_vin_datum: e.target.value })} />
            </div>
            {instFält('uppskattat_belopp', `Ungefärligt belopp (${inst.valuta})`, 'Innan raderna finns')}
            {instFält('uppskattade_flaskor', 'Ungefärligt antal flaskor', 'Innan raderna finns')}
            {([['producentansvarig', 'Producentansvarig'], ['logistikansvarig', 'Logistik och ekonomi']] as const).map(([f, t]) => (
              <div key={f}>
                <label style={etikett} htmlFor={`inst-${f}`}>{t}</label>
                <select id={`inst-${f}`} style={fält} value={inst[f]} onChange={e => setInst({ ...inst, [f]: e.target.value })}>
                  <option value="">Ingen</option>
                  {personer.map(x => <option key={x.id} value={x.id}>{x.namn}</option>)}
                </select>
              </div>
            ))}
          </div>
          <KursRad inköp={inköp} onSpara={kurs => kör(() => ändraInköp(id, { kurs }), 'Kunde inte spara kursen.')} />
          <label style={etikett} htmlFor="inst-anteckning">Anteckning</label>
          <textarea id="inst-anteckning" style={{ ...fält, minHeight: '60px', marginBottom: '14px' }} value={inst.anteckning}
            onChange={e => setInst({ ...inst, anteckning: e.target.value })} />
          <div style={{ display: 'flex', gap: '10px' }}>
            <button style={knapp} onClick={sparaInställningar}>Spara</button>
            <button style={{ ...knappSpök, marginLeft: 'auto', color: TEMA.inkFaint }} onClick={taBort}>Ta bort inköpet</button>
          </div>
        </div>
      )}

      {inköp.anteckning && !inst && (
        <Langtext text={inköp.anteckning} rader={3} style={{ fontSize: '13px', margin: '0 0 14px', color: TEMA.inkSoft }} />
      )}

      <Vik titel="Leveranser"
        åtgärd={<button style={länkknapp} onClick={() => kör(() => skapaLeverans(id), 'Kunde inte skapa leveransen.')}>+ Ny leverans</button>}>
          {inköp.leveranser.length === 0 && (
            <p style={{ fontSize: '13px', margin: 0 }}>Ingen leverans än. Den första tar med alla flaskor som förslag.</p>
          )}
          {inköp.leveranser.map(l => (
            <LeveransKort key={l.id} l={l} rader={inköp.rader} valuta={valuta} maxPerPall={inköp.producent.max_flaskor_per_pall}
              ladda={() => { ladda(); onÄndrat?.(); }} setFel={setFel} />
          ))}
      </Vik>

      <Vik titel="Betalningar" sammanfattning={förfallen ? 'Förfallen betalning' : undefined} varning={förfallen}>
        <Betalningar inköp={inköp} kör={kör} />
      </Vik>

      <Vik titel="Att göra" antal={öppnaBockar}
        öppen={öppnaBockar > 0}>
        <Bockar inköp={inköp} personer={personer} kör={kör} />
      </Vik>

    </div>
  );
}


// /inkop/lista/:id leder till producentkortet med inköpet öppet.
export default function InkopDetalj() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [fel, setFel] = useState<string | null>(null);
  useEffect(() => {
    hämtaEttInköp(id)
      .then(r => navigate(`/inkop/producenter/${r.data.producent.id}?inkop=${id}`, { replace: true }))
      .catch(e => setFel(felText(e, 'Kunde inte hitta inköpet.')));
  }, [id, navigate]);
  return <p style={sida}>{fel || 'Öppnar inköpet...'}</p>;
}
